import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Grid,
    Avatar,
    FormControlLabel,
    Switch,
    MenuItem,
    Snackbar
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import UpdateSportRequest from '../models/dtos/UpdateSportRequest';

interface SportEditModalProps {
    open: boolean;
    sport: FlashBetSportDto | null;
    onSave: (data: UpdateSportRequest) => void;
    onClose: () => void;
}

const SportEditModal: React.FC<SportEditModalProps> = ({ open, sport, onSave, onClose }) => {
    const [formData, setFormData] = useState<UpdateSportRequest>({
        name: '',
        photoUrl: '',
        localizations: {},
        enabled: true,
        questionTimeoutInSeconds: 30
    });
    const [localizationEntries, setLocalizationEntries] = useState<Array<{ lang: string; name: string }>>([]);
    const [errors, setErrors] = useState<{ name?: string; photoUrl?: string; questionTimeoutInSeconds?: string }>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (sport) {
            setFormData({
                name: sport.name,
                photoUrl: sport.photoUrl,
                localizations: sport.localizations || {},
                enabled: sport.enabled,
                questionTimeoutInSeconds: sport.questionTimeoutInSeconds || 30
            });
            
            // Convert localizations to array format for editing
            const entries = Object.entries(sport.localizations || {}).map(([lang, name]) => ({
                lang,
                name
            }));
            setLocalizationEntries(entries);
        } else {
            // Reset form
            setFormData({
                name: '',
                photoUrl: '',
                localizations: {},
                enabled: true,
                questionTimeoutInSeconds: 30
            });
            setLocalizationEntries([]);
        }
        setErrors({});
    }, [sport, open]);

    const validateForm = () => {
        const newErrors: { name?: string; photoUrl?: string; questionTimeoutInSeconds?: string } = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        
        if (!formData.photoUrl.trim()) {
            newErrors.photoUrl = 'Photo URL is required';
        }
        
        if (formData.questionTimeoutInSeconds <= 0) {
            newErrors.questionTimeoutInSeconds = 'Question duration must be greater than 0';
        } else if (formData.questionTimeoutInSeconds > 3600) {
            newErrors.questionTimeoutInSeconds = 'Question duration cannot exceed 3600 seconds';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        // Convert localization entries back to object format
        const localizations: { [key: string]: string } = {};
        localizationEntries.forEach(entry => {
            if (entry.lang.trim() && entry.name.trim()) {
                localizations[entry.lang.trim()] = entry.name.trim();
            }
        });

        const dataToSave: UpdateSportRequest = {
            ...formData,
            localizations
        };

        try {
            onSave(dataToSave);
        } catch (error) {
            console.error('Failed to save sport:', error);
            setErrorMessage('Failed to save sport changes. Please try again.');
        }
    };

    const handleInputChange = (field: keyof UpdateSportRequest) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        let value: string | boolean | number = event.target.value;
        
        if (field === 'enabled') {
            value = event.target.checked;
        } else if (field === 'questionTimeoutInSeconds') {
            value = parseInt(event.target.value) || 0;
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const addLocalizationEntry = () => {
        setLocalizationEntries(prev => [...prev, { lang: '', name: '' }]);
    };

    const removeLocalizationEntry = (index: number) => {
        setLocalizationEntries(prev => prev.filter((_, i) => i !== index));
    };

    const updateLocalizationEntry = (index: number, field: 'lang' | 'name', value: string) => {
        setLocalizationEntries(prev => prev.map((entry, i) => 
            i === index ? { ...entry, [field]: value } : entry
        ));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {sport ? 'Edit Sport' : 'Add Sport'}
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Sport Name *"
                                value={formData.name}
                                onChange={handleInputChange('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                                margin="normal"
                            />
                            
                            <TextField
                                fullWidth
                                label="Photo URL *"
                                value={formData.photoUrl}
                                onChange={handleInputChange('photoUrl')}
                                error={!!errors.photoUrl}
                                helperText={errors.photoUrl}
                                margin="normal"
                            />
                            
                            <TextField
                                fullWidth
                                label="Question answer timeout in Seconds *"
                                type="number"
                                value={formData.questionTimeoutInSeconds}
                                onChange={handleInputChange('questionTimeoutInSeconds')}
                                error={!!errors.questionTimeoutInSeconds}
                                helperText={errors.questionTimeoutInSeconds || 'Enter a value between 1 and 3600 seconds'}
                                margin="normal"
                                inputProps={{ min: 1, max: 3600 }}
                            />

                            <Box mt={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.enabled}
                                            onChange={handleInputChange('enabled')}
                                        />
                                    }
                                    label="Enabled"
                                />
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                <Avatar
                                    src={formData.photoUrl}
                                    alt={formData.name}
                                    sx={{ width: 100, height: 100 }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Box mt={3}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h6">Localizations</Typography>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={addLocalizationEntry}
                                variant="outlined"
                                size="small"
                            >
                                Add Language
                            </Button>
                        </Box>
                        
                        {localizationEntries.map((entry, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={2} mb={2}>
                                <TextField
                                    label="Language Code"
                                    select
                                    value={entry.lang}
                                    onChange={(e) => updateLocalizationEntry(index, 'lang', e.target.value)}
                                    size="small"
                                    sx={{ width: '150px' }}
                                >
                                    <MenuItem value="">Select language</MenuItem>
                                    <MenuItem value="de">German (de)</MenuItem>
                                    <MenuItem value="fr">French (fr)</MenuItem>
                                    <MenuItem value="it">Italian (it)</MenuItem>
                                    <MenuItem value="ro">Romanian (ro)</MenuItem>
                                    <MenuItem value="ru">Russian (ru)</MenuItem>
                                    <MenuItem value="es">Spanish (es)</MenuItem>
                                    <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                    <MenuItem value="pl">Polish (pl)</MenuItem>
                                    <MenuItem value="tr">Turkish (tr)</MenuItem>
                                </TextField>
                                <TextField
                                    label="Localized Name"
                                    value={entry.name}
                                    onChange={(e) => updateLocalizationEntry(index, 'name', e.target.value)}
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                />
                                <IconButton
                                    onClick={() => removeLocalizationEntry(index)}
                                    color="error"
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        
                        {localizationEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No localizations added. Click "Add Language" to add localized names.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">
                    Save
                </Button>
            </DialogActions>

            {/* Error Toast */}
            <Snackbar
                open={errorMessage !== null}
                autoHideDuration={4000}
                onClose={() => setErrorMessage(null)}
                message={errorMessage}
            />
        </Dialog>
    );
};

export default SportEditModal;